import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Drives all brand colors via CSS variables on :root, mirroring the pattern
 * used across the design system (see DESIGN.md "Theming"). There is no
 * theme API on the backend yet — colors are static defaults for now — but
 * every consumer already reads var(--theme-*), so swapping this store for
 * an API-backed one later requires no changes anywhere else.
 */
export const useThemeStore = defineStore('theme', () => {
  const defaultColors = {
    primary_color: '#16a34a',
    secondary_color: '#0f3d24',
  }

  const colors = ref({ ...defaultColors })

  const primaryColor = computed(() => colors.value.primary_color || defaultColors.primary_color)
  const secondaryColor = computed(() => colors.value.secondary_color || defaultColors.secondary_color)

  function loadTheme() {
    applyCSSVariables()
  }

  function setColors(next) {
    colors.value = { ...colors.value, ...next }
    applyCSSVariables()
  }

  function applyCSSVariables() {
    const root = document.documentElement

    root.style.setProperty('--theme-primary', primaryColor.value)
    root.style.setProperty('--theme-secondary', secondaryColor.value)
    root.style.setProperty('--theme-primary-rgb', hexToRgb(primaryColor.value))
    root.style.setProperty('--theme-secondary-rgb', hexToRgb(secondaryColor.value))

    root.style.setProperty('--theme-primary-hover', adjustBrightness(primaryColor.value, -0.12))
    root.style.setProperty('--theme-primary-active', adjustBrightness(primaryColor.value, -0.2))
    root.style.setProperty('--theme-primary-light', adjustBrightness(primaryColor.value, 0.85))
    root.style.setProperty('--theme-primary-lighter', adjustBrightness(primaryColor.value, 0.92))
    root.style.setProperty('--theme-primary-dark', adjustBrightness(primaryColor.value, -0.25))

    root.style.setProperty('--theme-secondary-hover', adjustBrightness(secondaryColor.value, 0.15))
    root.style.setProperty('--theme-secondary-active', adjustBrightness(secondaryColor.value, 0.25))
    root.style.setProperty('--theme-secondary-light', adjustBrightness(secondaryColor.value, 0.85))
    root.style.setProperty('--theme-secondary-lighter', adjustBrightness(secondaryColor.value, 0.92))

    // PrimeVue Aura primary palette override
    root.style.setProperty('--p-primary-color', primaryColor.value)
    root.style.setProperty('--p-primary-500', primaryColor.value)
    root.style.setProperty('--p-primary-contrast-color', '#ffffff')
    root.style.setProperty('--p-primary-50', adjustBrightness(primaryColor.value, 0.95))
    root.style.setProperty('--p-primary-100', adjustBrightness(primaryColor.value, 0.85))
    root.style.setProperty('--p-primary-200', adjustBrightness(primaryColor.value, 0.7))
    root.style.setProperty('--p-primary-300', adjustBrightness(primaryColor.value, 0.5))
    root.style.setProperty('--p-primary-400', adjustBrightness(primaryColor.value, 0.3))
    root.style.setProperty('--p-primary-600', adjustBrightness(primaryColor.value, -0.1))
    root.style.setProperty('--p-primary-700', adjustBrightness(primaryColor.value, -0.2))
    root.style.setProperty('--p-primary-800', adjustBrightness(primaryColor.value, -0.3))
    root.style.setProperty('--p-primary-900', adjustBrightness(primaryColor.value, -0.4))
  }

  function hexToRgb(hex) {
    if (!hex) return '0, 0, 0'
    hex = hex.replace('#', '')
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('')
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    return `${r}, ${g}, ${b}`
  }

  function adjustBrightness(hex, factor) {
    if (!hex) return '#000000'
    hex = hex.replace('#', '')
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('')
    let r = parseInt(hex.substring(0, 2), 16)
    let g = parseInt(hex.substring(2, 4), 16)
    let b = parseInt(hex.substring(4, 6), 16)

    if (factor > 0) {
      r = Math.round(r + (255 - r) * factor)
      g = Math.round(g + (255 - g) * factor)
      b = Math.round(b + (255 - b) * factor)
    } else {
      r = Math.round(r * (1 + factor))
      g = Math.round(g * (1 + factor))
      b = Math.round(b * (1 + factor))
    }

    r = Math.min(255, Math.max(0, r))
    g = Math.min(255, Math.max(0, g))
    b = Math.min(255, Math.max(0, b))

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  }

  return {
    colors,
    primaryColor,
    secondaryColor,
    loadTheme,
    setColors,
    applyCSSVariables,
  }
})
